import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Loader2 } from "lucide-react";
import NoteNode from "@/components/notes/NoteNode";
import NoteComposer from "@/components/notes/NoteComposer";

/**
 * OpportunityNotes — team-chat-style thread for a single opportunity with
 *   • full nested threading (parent_id chain, unlimited depth)
 *   • edit + soft-delete of your own notes
 *   • base64 file attachments (≤ 5 MB) rendered as inline download links
 */

// Read a File → base64 (strip the "data:...;base64," prefix)
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = () => reject(new Error("read failed"));
    r.readAsDataURL(file);
  });
}

export default function OpportunityNotes({ noticeId, currentUserId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);
  const [editOpen, setEditOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/opportunities/${noticeId}/notes`);
      setNotes(r.data.notes || []);
    } catch (e) {
      console.error("notes load failed", e);
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, [noticeId]);

  useEffect(() => { load(); }, [load]);

  // Group notes by parent_id for O(1) tree traversal.
  const childrenOf = useMemo(() => {
    const map = new Map();
    for (const n of notes) {
      const k = n.parent_id || "__root__";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(n);
    }
    return map;
  }, [notes]);

  const postNote = async (body, parentId, attachments) => {
    const payload = { body };
    if (parentId) payload.parent_id = parentId;
    setPosting(true);
    try {
      const r = await api.post(`/opportunities/${noticeId}/notes`, payload);
      if (attachments && attachments.length) {
        for (const f of attachments) {
          try {
            const b64 = await fileToBase64(f);
            await api.post(
              `/opportunities/${noticeId}/notes/${r.data.note_id}/attachments`,
              { filename: f.name, content_type: f.type || "application/octet-stream", data_base64: b64 },
            );
          } catch (attErr) {
            const msg = (attErr && attErr.response && attErr.response.data && attErr.response.data.detail) || ("Failed to upload " + f.name);
            toast.error(msg);
          }
        }
      }
      await load();
      return true;
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Failed to post note");
      return false;
    } finally {
      setPosting(false);
    }
  };

  const editNote = async (noteId, body) => {
    try {
      await api.patch(`/opportunities/${noticeId}/notes/${noteId}`, { body });
      setEditOpen(null);
      await load();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Failed to save edit");
    }
  };

  const deleteNote = async (noteId) => {
    if (!confirm("Delete this note? Replies to it will remain visible.")) return;
    try {
      await api.delete(`/opportunities/${noticeId}/notes/${noteId}`);
      await load();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Failed to delete note");
    }
  };

  const roots = childrenOf.get("__root__") || [];

  // DFS-flatten the tree so we render a linear list with `depth` attached to
  // each node. This avoids recursive JSX (which triggers a Babel stack-overflow
  // bug on some CRA versions) while preserving Reddit-style nested threading.
  const flatNotes = [];
  const walk = (parentKey, depth) => {
    const kids = childrenOf.get(parentKey) || [];
    for (const k of kids) {
      flatNotes.push({ note: k, depth });
      walk(k.note_id, depth + 1);
    }
  };
  walk("__root__", 0);

  return (
    <div data-testid="opportunity-notes">
      <NoteComposer
        placeholder="Share an update, question, or COR call log with the team…"
        onSubmit={(body, files) => postNote(body, null, files)}
        disabled={posting}
        testIdPrefix="root-note"
      />

      <div className="mt-6">
        {loading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2" data-testid="notes-loading">
            <Loader2 className="w-4 h-4 animate-spin" />Loading notes…
          </div>
        ) : roots.length === 0 ? (
          <div className="text-slate-500 text-sm flex items-center gap-2" data-testid="notes-empty">
            <MessageSquare className="w-4 h-4" />
            No notes yet — kick off the thread above.
          </div>
        ) : (
          <div className="space-y-3">
            {flatNotes.map(({ note, depth }) => (
              <NoteNode
                key={note.note_id}
                note={note}
                depth={depth}
                currentUserId={currentUserId}
                noticeId={noticeId}
                replyOpen={replyOpen}
                setReplyOpen={setReplyOpen}
                editOpen={editOpen}
                setEditOpen={setEditOpen}
                onReply={(pid, body, files) => postNote(body, pid, files).then((ok) => { if (ok) setReplyOpen(null); return ok; })}
                onEdit={editNote}
                onDelete={deleteNote}
                posting={posting}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
